// Build-time configuration for the web user client (spec §84 / §86).
//
// Vite statically replaces `import.meta.env.VITE_*` references at build
// time. The single source of truth for the API base URL in any non-dev
// build is therefore the `VITE_API_BASE_URL` env var, which
// `.github/workflows/web.yml` injects per job:
//
//   deploy-staging    → VITE_API_BASE_URL=https://api-staging.columbx.com
//   deploy-production → VITE_API_BASE_URL=https://api.columbx.com
//
// If the CI job forgets to inject the variable, `vite build` itself does
// not fail — but this module throws so the build pipeline surfaces the
// misconfiguration rather than silently routing to the production
// backend. This mirrors the design in `apps/admin/src/config/appConfig.ts`.
//
// `detectEnvironment()` exists for two narrow purposes:
//   1. Populate the `appConfig.environment` field (for UI badges / logs /
//      future audit logging). It is NOT consulted when choosing the API
//      base URL.
//   2. Serve as a defensive last-resort hint if, in some unforeseen
//      runtime, `VITE_API_BASE_URL` is missing. It is never the primary
//      routing mechanism for Cloudflare Pages Preview / Production.
//
// The previous `<meta name="app:api-base-url">` escape hatch has been
// removed to keep a single source of truth (matches Admin's policy).
export interface AppConfig {
  apiBaseUrl: string;
  apiVersion: string;
  environment: 'local' | 'staging' | 'production';
  webOrigin: string;
}

const DEFAULT_WEB_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

function resolveWebApiBaseUrl(): string {
  const fromVite = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromVite === 'string' && fromVite.length > 0) {
    return fromVite;
  }

  if (import.meta.env.DEV) {
    return 'http://localhost:8787';
  }

  throw new Error(
    '[web] VITE_API_BASE_URL is not configured. ' +
      'See .github/workflows/web.yml — the `npm run build` step must ' +
      'receive it via `env:`. ' +
      'No silent fallback to the production backend is permitted.',
  );
}

/**
 * Best-effort environment label for logs / UI badges. Never participates
 * in API URL selection.
 *
 *   localhost / 127.0.0.1              → local
 *   <id>.fieldreport-web.pages.dev     → staging (Cloudflare Pages Preview)
 *   fieldreport-web.pages.dev          → production (Pages root, defensive)
 *   staging.*                          → staging (legacy custom-domain rule)
 *   anything else                      → production
 */
function detectEnvironment(): 'local' | 'staging' | 'production' {
  if (typeof window === 'undefined') return 'local';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host === 'fieldreport-web.pages.dev') return 'production';
  if (host.endsWith('.fieldreport-web.pages.dev')) return 'staging';
  if (host.startsWith('staging.')) return 'staging';
  return 'production';
}

const inferredEnv = detectEnvironment();

export const appConfig: AppConfig = {
  apiBaseUrl: resolveWebApiBaseUrl(),
  apiVersion: (import.meta.env.VITE_API_VERSION as string | undefined) ?? 'v1',
  environment:
    (import.meta.env.VITE_ENVIRONMENT as 'local' | 'staging' | 'production' | undefined) ??
    inferredEnv,
  webOrigin:
    (import.meta.env.VITE_WEB_ORIGIN as string | undefined) ??
    (typeof window !== 'undefined' ? window.location.origin : DEFAULT_WEB_ORIGIN),
};
